import { Component, inject, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogRef } from '@angular/material/dialog';
import { ToolbarService } from '../../../services/toolbar.service';
import { UserService } from '../../../services/user.service';
import { DocumentReference, PhotoReference } from '../../../model/job.model';
import { EscapeDialogHelper } from '../../../helpers/escape-dialog.helper';
import { MatIcon } from '@angular/material/icon';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatPrefix } from '@angular/material/form-field';
import { MatDivider } from '@angular/material/divider';
import { MatButton } from '@angular/material/button';
import { DocumentInvoiceItem, DocumentQuoteItem } from '../../../model/document.model';

@Component({
  selector: 'app-submit-document',
  templateUrl: './submit-document.component.html',
  imports: [MatIcon, MatProgressBar, MatPrefix, MatDivider, MatButton, MatDialogClose]
})
export class SubmitDocumentComponent implements OnInit {
  data: any = inject(MAT_DIALOG_DATA);
  #dialogRef = inject(MatDialogRef<SubmitDocumentComponent>);
  #escapeDialogHelper = inject(EscapeDialogHelper);
  #toolbarService = inject(ToolbarService);
  #userService = inject(UserService);

  submitting = signal(false);
  errorMessage = signal(false);
  assessorName = this.data.assessorName;
  sendingDocumentsMessage = '';
  hasRepairerCommentWarning = false;

  ngOnInit(): void {
    this.sendingDocumentsMessage = this.#createSendingMessage();
    this.hasRepairerCommentWarning = this.#hasRepairerCommentWarning();
    this.#escapeDialogHelper.escapeDialog(this.#dialogRef);
  }

  #hasRepairerCommentWarning(): boolean {
    const repairerComment = this.data.unSubmittedInvoice?.document?.repairerComment;

    if (!repairerComment) {
      return false;
    }

    const pattern = new RegExp(`NOTICE[\\s\\S]*ASSIGNMENT`, 'i');
    return (
      pattern.test(repairerComment) || repairerComment.includes('10378199') || repairerComment.includes('14027946')
    );
  }

  #createPhotoMessage(photos: PhotoReference[]): string {
    const numPhotos = photos.length;
    return numPhotos === 1 ? '1 photo' : `${numPhotos} photos`;
  }

  #createDocsMessage(docs: DocumentReference[]): string {
    const numDocs = docs.length;
    return numDocs === 1 ? '1 document' : `${numDocs} documents`;
  }

  #createQuoteMessage(quote: DocumentQuoteItem): string {
    return 'Quote # ' + quote.document.quoteNumber;
  }

  #createInvoiceMessage(invoice: DocumentInvoiceItem): string {
    return 'Invoice # ' + invoice.document.invoiceNumber;
  }

  #createGICOPMessage(type: string): string {
    return `a ${type} notification`;
  }

  #createSendingMessage(): string {
    const { job, unSubmittedInvoice, unSubmittedQuote, unSubmittedPhotos, unSubmittedDocs } = this.data;
    const items = [
      unSubmittedInvoice && this.#createInvoiceMessage(unSubmittedInvoice),
      unSubmittedQuote && this.#createQuoteMessage(unSubmittedQuote),
      unSubmittedPhotos?.length && this.#createPhotoMessage(unSubmittedPhotos),
      unSubmittedDocs?.length && this.#createDocsMessage(unSubmittedDocs),
      this.data.unSubmittedDiary && 'a diary',
      job?.enableSelfAuthorityCurtain && 'a Self-Authorise notification',
      job?.booking && 'a booking update',
      this.data.unSubmittedCustomerContact && this.#createGICOPMessage('Customer Contact'),
      this.data.unSubmittedCustomerFeedback && this.#createGICOPMessage('Customer Feedback'),
      this.data.unSubmittedPotentialBreachOfCode && this.#createGICOPMessage('Potential Breach of Code'),
      this.data.unSubmittedCustomerVulnerability && this.#createGICOPMessage('Customer Vulnerability'),
      job?.noQuoteResponse &&
        job?.noQuoteResponseAssessmentAction &&
        `the assessment action '${job.noQuoteResponseAssessmentAction}'`
    ].filter(Boolean);

    const mergedMessage =
      items.length === 1 ? items[0] : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
    const message = mergedMessage.charAt(0).toUpperCase() + mergedMessage.slice(1);

    return `${message} will be sent to ${this.assessorName}.`;
  }

  submitJob(): void {
    this.submitting.set(true);

    this.#toolbarService.setCloudState('SAVING');
    this.#userService.submitJob(this.data.job.jobId).subscribe({
      next: () => this.#toolbarService.setCloudState('RESTING'),
      error: () => {
        this.submitting.set(false);
        this.errorMessage.set(true);
        this.#toolbarService.setCloudState('RESTING');
      },
      complete: () => {
        this.submitting.set(false);
        this.#dialogRef.close('Success');
      }
    });
  }
}
